/* eslint-disable @typescript-eslint/no-explicit-any */
import { DndContext, DragOverlay, closestCorners } from "@dnd-kit/core";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";
import { deleteData } from "../../Services/deleteData";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import createConnectionForWebsocket from "../../Utils/utils";
import { TaskModel, TaskStatus } from "../../Models/Models";
import { componentNames } from "../../Utils/Constants";
import { urlEnum } from "../../Utils/urlEnum";
import styles from "../../Styles/tasksDashboard.module.css";
import { Vocabulary } from "../../Utils/Vocabulary";
import {
  buildUrl,
  getWeekRange,
  TaskFilters,
  toQueryParams,
} from "../../Utils/tasksUtils";
import { TASKS_MAX_PER_PAGE } from "../../Utils/tasksConstants";
import { useKanbanDrag } from "../../Utils/useKanbanDrag";
import { objectToFormData } from "../../Utils/logoUploadHelper";
import TaskDocGateDialog from "./TaskDocGateDialog";
import TaskForm from "./TaskForm";
import { TaskCardOverlay } from "./TaskCard";
import TasksKanban from "./TasksKanban";
import FilePreview from "../../Utils/FilePreview";

type State = {
  tasks: TaskModel[];
  loading: boolean;
  formOpen: boolean;
  selectedTask: TaskModel | null;
};

type DocGateState = {
  open: boolean;
  task: TaskModel | null;
  pendingStatus: TaskStatus | null;
  fromDrag: boolean;
};

type Props = {
  embedded?: boolean;
};

export default function TasksDashboard({ embedded }: Props) {
  const [state, setState] = useState<State>({
    tasks: [],
    loading: true,
    formOpen: false,
    selectedTask: null,
  });
  const [docGate, setDocGate] = useState<DocGateState>({
    open: false,
    task: null,
    pendingStatus: null,
    fromDrag: false,
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchQuery, setSearchQuery] = useState("");
  const [filters] = useState<TaskFilters>({
    period: embedded ? "week" : "all",
    ...getWeekRange(),
    priority: "",
    status: "",
    assigneeId: "",
    createdById: "",
    view: "board",
  });

  const [newTaskIds, setNewTaskIds] = useState<Set<number>>(new Set());

  const tasksRef = useRef<TaskModel[]>([]);
  tasksRef.current = state.tasks;

  function setTasks(updater: (prev: TaskModel[]) => TaskModel[]) {
    setState((prev) => ({ ...prev, tasks: updater(prev.tasks) }));
  }

  function persistStatus(taskId: number, status: TaskStatus) {
    if (status === "completed") {
      const task = tasksRef.current.find((t) => t.id === taskId);
      if (task?.requires_document && !task.document_path) {
        setDocGate({ open: true, task, pendingStatus: status, fromDrag: true });
        return;
      }
    }
    postData(`${urlEnum.tasks}/${taskId}`, { status }).catch(() =>
      fetchTasks(searchQuery, filters)
    );
  }

  const { draggingTask, sensors, handleDragStart, handleDragOver, handleDragEnd } =
    useKanbanDrag(state.tasks, setTasks, persistStatus);

  function fetchTasks(search: string, currentFilters: TaskFilters) {
    setState((prev) => ({ ...prev, loading: true }));
    getData(
      buildUrl(
        urlEnum.tasks,
        toQueryParams(search, currentFilters, { perPage: TASKS_MAX_PER_PAGE })
      )
    )
      .then((res: any) => {
        setState((prev) => ({
          ...prev,
          tasks: res?.data?.tasks ?? res?.data ?? [],
          loading: false,
        }));
      })
      .catch(() => setState((prev) => ({ ...prev, loading: false })));
  }

  useEffect(() => {
    fetchTasks("", filters);
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem("useId");
    if (!userId) return;

    const pusher = createConnectionForWebsocket();
    const channel = pusher.subscribe(`private-tasks.${userId}`);

    channel.bind("task-assigned", (data: any) => {
      const task: TaskModel = data?.task;
      if (!task?.id) return;

      setTasks((prev) => {
        if (prev.some((t) => t.id === task.id)) return prev;
        return [task, ...prev];
      });

      setNewTaskIds((prev) => new Set(Array.from(prev).concat(task.id!)));

      setTimeout(() => {
        setNewTaskIds((prev) => {
          const next = new Set(prev);
          next.delete(task.id!);
          return next;
        });
      }, 1000);
    });

    return () => {
      pusher.unsubscribe(`private-tasks.${userId}`);
    };
  }, []);

  function openCreate(status: TaskStatus = "not_started") {
    const template = new TaskModel();
    template.status = status;
    setState((prev) => ({ ...prev, formOpen: true, selectedTask: template }));
  }

  function openEdit(task: TaskModel) {
    setState((prev) => ({ ...prev, formOpen: true, selectedTask: task }));
  }

  function closeForm() {
    setState((prev) => ({ ...prev, formOpen: false, selectedTask: null }));
  }

  function handleSave(values: TaskModel) {
    const isEditing = !!values.id;
    const request = isEditing
      ? postData(`${urlEnum.tasks}/${values.id}`, values)
      : postData(urlEnum.tasks, values);
    request
      .then(() => {
        closeForm();
        fetchTasks(searchQuery, filters);
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch((_) => {});
  }

  function handleStatusChange(task: TaskModel, status: TaskStatus) {
    if (status === "completed" && task.requires_document && !task.document_path) {
      setDocGate({ open: true, task, pendingStatus: status, fromDrag: false });
      return;
    }
    setState((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t) => (t.id === task.id ? { ...t, status } : t)),
    }));
    postData(`${urlEnum.tasks}/${task.id}`, { status }).catch(() =>
      fetchTasks(searchQuery, filters)
    );
  }

  function handleDocGateClose() {
    if (docGate.fromDrag) fetchTasks(searchQuery, filters);
    setDocGate({ open: false, task: null, pendingStatus: null, fromDrag: false });
  }

  async function handleDocGateConfirm(file: File) {
    const { task, pendingStatus } = docGate;
    if (!task?.id || !pendingStatus) return;

    const formData = objectToFormData({ ...task, status: pendingStatus });
    formData.append("document", file);

    await postData(`${urlEnum.tasks}/${task.id}`, formData);
    setDocGate({ open: false, task: null, pendingStatus: null, fromDrag: false });
    fetchTasks(searchQuery, filters);
  }

  function handleDelete(task: TaskModel) {
    if (!task.id) return;
    deleteData(`${urlEnum.tasks}/${task.id}`).then(() => {
      setState((prev) => ({
        ...prev,
        tasks: prev.tasks.filter((t) => t.id !== task.id),
      }));
    });
  }

  return (
    <>
      {isMobile ? (
        <div className={styles.boardHeaderMobile}>
          <div className={styles.boardHeaderTop}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              disableElevation
              onClick={() => openCreate()}
              style={{ borderRadius: 8, textTransform: "none", fontWeight: 600, color: "#fff", whiteSpace: "nowrap" }}
            >
              {Vocabulary.taskNew}
            </Button>
          </div>
          <TextField
            size="small"
            placeholder={`${Vocabulary.search}…`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchTasks(searchQuery, filters)}
            className={styles.searchInputMobile}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" style={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearchQuery(""); fetchTasks("", filters); }}>
                    <CloseIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </div>
      ) : (
        <div className={styles.boardHeader}>
          <TextField
            size="small"
            placeholder={`${Vocabulary.search}…`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchTasks(searchQuery, filters)}
            className={styles.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" style={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearchQuery(""); fetchTasks("", filters); }}>
                    <CloseIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <div className={styles.boardHeaderRight}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              disableElevation
              onClick={() => openCreate()}
              style={{ borderRadius: 8, textTransform: "none", fontWeight: 600, color: "#fff", whiteSpace: "nowrap", padding: "8px 32px" }}
            >
              {Vocabulary.taskNew}
            </Button>
          </div>
        </div>
      )}

      {state.loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
          <CircularProgress />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <TasksKanban
            tasks={state.tasks}
            onEdit={openEdit}
            onDelete={handleDelete}
            onAdd={openCreate}
            onStatusChange={handleStatusChange}
            compact={embedded}
            newTaskIds={newTaskIds}
          />
          <DragOverlay dropAnimation={{ duration: 180, easing: "ease" }}>
            {draggingTask ? <TaskCardOverlay task={draggingTask} /> : null}
          </DragOverlay>
        </DndContext>
      )}

      {state.formOpen && (
        <TaskForm
          name={componentNames.TaskForm}
          open={state.formOpen}
          task={state.selectedTask}
          onClose={closeForm}
          onSave={handleSave}
        />
      )}

      {docGate.open && docGate.task && (
        <TaskDocGateDialog
          open={docGate.open}
          task={docGate.task}
          onClose={handleDocGateClose}
          onConfirm={handleDocGateConfirm}
        />
      )}

      <FilePreview key={state.tasks.length} />
    </>
  );
}
